import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Loader2, Inbox, Mail, Phone, User, Calendar, Check, Clock,
  AlertTriangle, Trash2, Filter, ArrowLeft, MessageSquare, RefreshCw,
} from 'lucide-react';
import api from '../../services/api';

/**
 * Contact Inbox — lists messages submitted through the public contact form
 * and lets staff triage them (new → in_progress → resolved/spam).
 *
 * Messages are tenant-scoped: the backend filters by `req.tenantId` so each
 * lab only sees their own inquiries.
 */

const STATUS_META = {
  new:         { label: 'جديد',       color: 'bg-blue-100 text-blue-700 border-blue-200',   icon: Inbox },
  in_progress: { label: 'قيد المتابعة', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  resolved:    { label: 'تم الرد',     color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: Check },
  spam:        { label: 'مزعج',        color: 'bg-slate-100 text-slate-500 border-slate-200', icon: AlertTriangle },
};

function formatWhen(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('ar-SA', { year: 'numeric', month: 'short', day: 'numeric' })
      + ' • '
      + d.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

const ContactInbox = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const res = await api.get(`/contact-messages${params}`);
      const data = res.data?.data || {};
      setItems(Array.isArray(data.items) ? data.items : []);
      setUnreadCount(Number(data.unreadCount) || 0);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'تعذّر تحميل الرسائل');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (msg, status) => {
    setUpdating(true);
    try {
      const res = await api.patch(`/contact-messages/${msg._id}`, { status });
      const updated = res.data?.data;
      setItems((prev) => prev.map((m) => (m._id === msg._id ? updated : m)));
      if (selected?._id === msg._id) setSelected(updated);
      toast.success('تم التحديث');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'تعذّر التحديث');
    } finally {
      setUpdating(false);
    }
  };

  const remove = async (msg) => {
    if (!window.confirm('حذف هذه الرسالة نهائياً؟')) return;
    setUpdating(true);
    try {
      await api.delete(`/contact-messages/${msg._id}`);
      setItems((prev) => prev.filter((m) => m._id !== msg._id));
      if (selected?._id === msg._id) setSelected(null);
      toast.success('تم الحذف');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'تعذّر الحذف');
    } finally {
      setUpdating(false);
    }
  };

  const filterTabs = useMemo(() => [
    { key: '',             label: 'الكل',        icon: Inbox },
    { key: 'new',          label: 'جديد',        icon: Inbox,          badge: unreadCount },
    { key: 'in_progress',  label: 'قيد المتابعة', icon: Clock },
    { key: 'resolved',     label: 'تم الرد',     icon: Check },
    { key: 'spam',         label: 'مزعج',        icon: AlertTriangle },
  ], [unreadCount]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shrink-0">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-extrabold text-slate-900">صندوق الرسائل</h1>
              <p className="text-xs text-slate-500 mt-0.5">الرسائل الواردة من نموذج "تواصل معنا" في موقعك.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-slate-600 text-sm hover:bg-white transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            تحديث
          </button>
        </div>

        {/* Filter tabs */}
        <div className="flex items-center gap-2 mb-5 overflow-x-auto">
          {filterTabs.map((tab) => {
            const Icon = tab.icon;
            const active = statusFilter === tab.key;
            return (
              <button
                key={tab.key || 'all'}
                type="button"
                onClick={() => setStatusFilter(tab.key)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors shrink-0 ${
                  active
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {tab.badge > 0 && (
                  <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-[10px] font-bold ${
                    active ? 'bg-white/20 text-white' : 'bg-blue-600 text-white'
                  }`}>{tab.badge}</span>
                )}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-5">
          {/* List */}
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm min-h-[60vh]">
            {loading ? (
              <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-blue-600 animate-spin" /></div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                <Inbox className="w-10 h-10 mb-3" />
                <p className="text-sm">لا توجد رسائل {statusFilter && 'بهذه الحالة'}</p>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {items.map((m) => {
                  const meta = STATUS_META[m.status] || STATUS_META.new;
                  const isSelected = selected?._id === m._id;
                  return (
                    <li
                      key={m._id}
                      onClick={() => setSelected(m)}
                      className={`p-4 cursor-pointer transition-colors ${
                        isSelected ? 'bg-blue-50 border-r-4 border-blue-600' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-bold text-slate-900 text-sm truncate">{m.name}</span>
                          {m.status === 'new' && (
                            <span className="w-2 h-2 rounded-full bg-blue-600 shrink-0" title="لم تُقرأ" />
                          )}
                        </div>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${meta.color} shrink-0`}>
                          {meta.label}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 truncate mb-1">{m.subject || m.message.slice(0, 80)}</p>
                      <p className="text-[11px] text-slate-400">{formatWhen(m.createdAt)}</p>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Detail */}
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            {!selected ? (
              <div className="flex flex-col items-center justify-center h-full py-20 text-slate-400">
                <Mail className="w-12 h-12 mb-3" />
                <p className="text-sm">اختر رسالة لعرض تفاصيلها</p>
              </div>
            ) : (
              <div className="p-6">
                <div className="flex items-start justify-between gap-3 mb-5 pb-5 border-b border-slate-100">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">{selected.name}</h2>
                    <p className="text-xs text-slate-400 mt-1">{formatWhen(selected.createdAt)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => remove(selected)}
                    disabled={updating}
                    className="p-2 rounded-xl text-red-500 hover:bg-red-50 disabled:opacity-50"
                    title="حذف"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Contact block */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                  {selected.email && (
                    <a href={`mailto:${selected.email}`}
                       className="flex items-center gap-2 p-3 rounded-xl border border-slate-200 text-sm hover:bg-slate-50 transition-colors">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <span dir="ltr" className="truncate">{selected.email}</span>
                    </a>
                  )}
                  {selected.phone && (
                    <a href={`tel:${selected.phone}`}
                       className="flex items-center gap-2 p-3 rounded-xl border border-slate-200 text-sm hover:bg-slate-50 transition-colors">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span dir="ltr" className="truncate">{selected.phone}</span>
                    </a>
                  )}
                </div>

                {selected.subject && (
                  <div className="mb-4">
                    <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">الموضوع</div>
                    <p className="text-sm text-slate-700 font-semibold">{selected.subject}</p>
                  </div>
                )}

                <div className="mb-6">
                  <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">الرسالة</div>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap bg-slate-50 rounded-xl p-4">
                    {selected.message}
                  </p>
                </div>

                {/* Status actions */}
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-semibold text-slate-500 mr-1">تغيير الحالة:</span>
                  {Object.entries(STATUS_META).map(([key, meta]) => {
                    const Icon = meta.icon;
                    const isCurrent = selected.status === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => !isCurrent && updateStatus(selected, key)}
                        disabled={updating || isCurrent}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                          isCurrent
                            ? `${meta.color} cursor-default`
                            : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {meta.label}
                      </button>
                    );
                  })}
                </div>

                {/* Referer / UA for spam review */}
                {(selected.referer || selected.ipAddress) && (
                  <div className="mt-6 pt-4 border-t border-slate-100 text-[10px] text-slate-400 space-y-1" dir="ltr">
                    {selected.referer && <div>referer: {selected.referer}</div>}
                    {selected.ipAddress && <div>IP: {selected.ipAddress}</div>}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactInbox;
